import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { ZodIssue } from 'zod';
import { ZodError, type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3, any as __eliadmin_zod_4 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

export const createOrderSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: __eliadmin_zod_1().min(8, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      address: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      city: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      postcode: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      country: __eliadmin_zod_0({ id: __eliadmin_zod_1().min(1).or(__eliadmin_zod_2()) }),

      company_buy: __eliadmin_zod_3().default(false),
      // if company_buy is true
      company: __eliadmin_zod_1().optional(),
      ico: __eliadmin_zod_1().optional(),
      dic: __eliadmin_zod_1().optional(),
      ic_dph: __eliadmin_zod_1().optional(),
      company_address: __eliadmin_zod_1().optional(),
      company_street_number: __eliadmin_zod_1().optional(),
      company_postcode: __eliadmin_zod_1().optional(),
      company_city: __eliadmin_zod_1().optional(),

      different_delivery_address: __eliadmin_zod_3().default(false),
      hide_different_delivery_address: __eliadmin_zod_3().default(false),
      region: __eliadmin_zod_4().optional().nullable(),

      address2: __eliadmin_zod_1().optional().nullable(),
      street_number2: __eliadmin_zod_1().optional().nullable(),
      city2: __eliadmin_zod_1().optional().nullable(),
      postcode2: __eliadmin_zod_1().optional().nullable(),
      country2: __eliadmin_zod_0({ id: __eliadmin_zod_4().optional() }).optional().nullable(),

      gdpr: __eliadmin_zod_3()
        .default(false)
        .refine(
          value => value === true,
          VM.TOS_VALIDATION_MESSAGE() + '. ' + VM.GDPR_VALIDATION_MESSAGE(),
        ),
      // must be true
      // conditions: z
      //   .boolean()
      //   .default(false)
      //   .refine(value => value === true),
      newsletter: __eliadmin_zod_3().default(false).optional(),
      create_account: __eliadmin_zod_3().default(false).optional(),

      company_name: __eliadmin_zod_1().optional(),
      iban: __eliadmin_zod_1().optional(),

      password: __eliadmin_zod_1().optional(),
      password_confirmation: __eliadmin_zod_1().optional(),

      note: __eliadmin_zod_1().optional(),

      return_url: __eliadmin_zod_1().optional(),
      newsletter_return_url: __eliadmin_zod_1(),
      check_status_url: __eliadmin_zod_1(),

      is_wholesaler: __eliadmin_zod_3().optional(),
      wholesaler_order_type: __eliadmin_zod_1().default('storage'),

      dropship_name: __eliadmin_zod_1().optional(),
      dropship_surname: __eliadmin_zod_1().optional(),
      dropship_email: __eliadmin_zod_1().optional(),
      dropship_phone: __eliadmin_zod_1().optional(),
      dropship_address: __eliadmin_zod_1().optional(),
      dropship_street_number: __eliadmin_zod_1().optional(),
      dropship_city: __eliadmin_zod_1().optional(),
      dropship_postcode: __eliadmin_zod_1().optional(),
      dropship_country: __eliadmin_zod_0({ id: __eliadmin_zod_1().or(__eliadmin_zod_2()) }).optional(),

      send_heureka_mail: __eliadmin_zod_3().optional(),
    })
    .refine(
      data => {
        const errors: ZodIssue[] = [];
        if (data.wholesaler_order_type === 'dropship') {
          if (!data.dropship_name || data.dropship_name.length === 0) {
            errors.push({
              path: ['dropship_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_surname || data.dropship_surname.length === 0) {
            errors.push({
              path: ['dropship_surname'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_email || data.dropship_email.length === 0) {
            errors.push({
              path: ['dropship_email'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_phone || data.dropship_phone.length < 5) {
            errors.push({
              path: ['dropship_phone'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_address || data.dropship_address.length === 0) {
            errors.push({
              path: ['dropship_address'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_street_number || data.dropship_street_number.length === 0) {
            errors.push({
              path: ['dropship_street_number'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_city || data.dropship_city.length === 0) {
            errors.push({
              path: ['dropship_city'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_postcode || data.dropship_postcode.length === 0) {
            errors.push({
              path: ['dropship_postcode'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_country || data.dropship_country.id.toString().length === 0) {
            errors.push({
              path: ['dropship_country.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_name || data.company_name.length === 0) {
            errors.push({
              path: ['company_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.different_delivery_address) {
          if (!data.address2 || data.address2.length === 0) {
            errors.push({
              path: ['address2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.street_number2 || data.street_number2.length === 0) {
            errors.push({
              path: ['street_number2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.city2 || data.city2.length === 0) {
            errors.push({
              path: ['city2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.postcode2 || data.postcode2.length === 0) {
            errors.push({
              path: ['postcode2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.country2 || data.country2.id?.toString().length === 0) {
            errors.push({
              path: ['country2.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.company_buy) {
          if (!data.ico || data.ico.length === 0) {
            errors.push({
              path: ['ico'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_address || data.company_address.length === 0) {
            errors.push({
              path: ['company_address'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_street_number || data.company_street_number.length === 0) {
            errors.push({
              path: ['company_street_number'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_postcode || data.company_postcode.length === 0) {
            errors.push({
              path: ['company_postcode'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_city || data.company_city.length === 0) {
            errors.push({
              path: ['company_city'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (errors.length > 0) {
          throw new ZodError(errors);
        }

        return true;
      },
      {
        path: ['wholesaler_order_type'],
      },
    );

export type CreateOrderFormValues = z.infer<ReturnType<typeof createOrderSchema>>;
